import type { InvoiceData } from '@shared/types';
import type { DocumentReference, Transaction } from 'firebase/firestore';

const currentModelInvoiceStatuses = ['finalized', 'voided'] as const;

export const isCurrentModelInvoice = (invoice: InvoiceData): boolean =>
  currentModelInvoiceStatuses.includes(
    invoice.status as (typeof currentModelInvoiceStatuses)[number],
  ) && invoice.schemaVersion === 3;

const datesMatch = (left?: Date, right?: Date): boolean =>
  left?.getTime() === right?.getTime();

const hasSameLifecycleFields = (
  existingInvoice: InvoiceData,
  replacementInvoice: InvoiceData,
): boolean =>
  existingInvoice.status === replacementInvoice.status &&
  existingInvoice.firmId === replacementInvoice.firmId &&
  existingInvoice.matterId === replacementInvoice.matterId &&
  existingInvoice.invoiceNumber === replacementInvoice.invoiceNumber &&
  existingInvoice.schemaVersion === replacementInvoice.schemaVersion &&
  datesMatch(
    existingInvoice.createdTimestamp,
    replacementInvoice.createdTimestamp,
  ) &&
  datesMatch(
    existingInvoice.finalizedTimestamp,
    replacementInvoice.finalizedTimestamp,
  ) &&
  datesMatch(existingInvoice.voidedAt, replacementInvoice.voidedAt) &&
  existingInvoice.voidedBy === replacementInvoice.voidedBy &&
  existingInvoice.voidReason === replacementInvoice.voidReason;

export const setInvoiceWithoutLifecycleTransitionInTransaction = async (
  transaction: Transaction,
  invoiceRef: DocumentReference<InvoiceData>,
  data: InvoiceData,
): Promise<void> => {
  const existingInvoice = (await transaction.get(invoiceRef)).data();
  const isExistingCurrentModelInvoice =
    existingInvoice !== undefined && isCurrentModelInvoice(existingInvoice);
  const isReplacementCurrentModelInvoice = isCurrentModelInvoice(data);

  if (
    isExistingCurrentModelInvoice !== isReplacementCurrentModelInvoice ||
    (isExistingCurrentModelInvoice &&
      !hasSameLifecycleFields(existingInvoice, data))
  ) {
    throw new Error(
      'Current-model invoice lifecycle changes must use a dedicated lifecycle action.',
    );
  }

  transaction.set(invoiceRef, data);
};

export const deleteNonCurrentModelInvoiceInTransaction = async (
  transaction: Transaction,
  invoiceRef: DocumentReference<InvoiceData>,
): Promise<void> => {
  const invoice = (await transaction.get(invoiceRef)).data();
  if (invoice !== undefined && isCurrentModelInvoice(invoice)) {
    throw new Error(
      'Current-model invoices cannot be hard-deleted. Void or revert finalization instead.',
    );
  }

  transaction.delete(invoiceRef);
};
